import { useState, useMemo } from 'react'
import { useVideos } from '../hooks/useVideos'
import VideoCard from '../components/VideoCard'

export default function VideosPage() {
  const { videos, loading, error, refreshVideos, deleteVideo, refreshVideoUrl } = useVideos()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Filtrar e ordenar vídeos
  const filteredAndSortedVideos = useMemo(() => {
    let filtered = videos.filter(video =>
      video.name.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return filtered.sort((a, b) => {
      let compareValue = 0
      
      switch (sortBy) {
        case 'name':
          compareValue = a.name.localeCompare(b.name)
          break
        case 'date':
          compareValue = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
        case 'size':
          compareValue = a.size - b.size
          break
        default:
          compareValue = 0
      }

      return sortOrder === 'asc' ? compareValue : -compareValue
    })
  }, [videos, searchTerm, sortBy, sortOrder])

  const handleSort = (newSortBy: 'name' | 'date' | 'size') => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(newSortBy)
      setSortOrder('desc')
    }
  }

  const formatTotalSize = (videos: typeof filteredAndSortedVideos) => {
    const totalBytes = videos.reduce((acc, video) => acc + video.size, 0)
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(totalBytes) / Math.log(k))
    return parseFloat((totalBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (loading && videos.length === 0) {
    return (
      <div className="min-h-screen page-bg">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Carregando vídeos...
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Buscando seus vídeos no bucket do Supabase
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen page-bg">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Erro ao carregar vídeos
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {error}
              </p>
              <button
                onClick={refreshVideos}
                className="btn-primary px-6 py-2"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Carregando...
                  </div>
                ) : (
                  'Tentar novamente'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen page-bg">
      <div className="container mx-auto px-4 py-8">
        {/* Cabeçalho */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Meus Vídeos
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                {filteredAndSortedVideos.length} vídeo{filteredAndSortedVideos.length !== 1 ? 's' : ''} 
                {filteredAndSortedVideos.length > 0 && ` • ${formatTotalSize(filteredAndSortedVideos)}`}
              </p>
            </div>
            
            <button
              onClick={refreshVideos}
              className="btn-secondary px-4 py-2 mt-4 sm:mt-0"
              disabled={loading}
              title="Atualizar lista"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Atualizando...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Atualizar
                </div>
              )}
            </button>
          </div>

          {/* Barra de busca e filtros */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* Busca */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar vídeos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input w-full pl-10"
                />
                <svg 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Ordenação */}
            <div className="flex gap-2">
              <button
                onClick={() => handleSort('name')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  sortBy === 'name'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Nome {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('date')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  sortBy === 'date'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Data {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('size')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  sortBy === 'size'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Tamanho {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
            </div>
          </div>
        </div>

        {/* Lista de vídeos */}
        {filteredAndSortedVideos.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {searchTerm ? 'Nenhum vídeo encontrado' : 'Nenhum vídeo disponível'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {searchTerm 
                ? `Nenhum vídeo corresponde à busca "${searchTerm}"`
                : 'Faça upload de vídeos para o bucket "videos" no Supabase para vê-los aqui'
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="btn-ghost mt-4 px-4 py-2"
              >
                Limpar busca
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredAndSortedVideos.map((video) => (
              <VideoCard
                key={video.id}
                video={video}
                onDelete={deleteVideo}
                onRefreshUrl={refreshVideoUrl}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
